/**
 * /ninety/knowledge — the EOS Knowledge doc store (F58 Phase 5).
 *
 * Left: a folder tree + the docs inside the selected folder. Right: the doc
 * viewer rendering its markdown body, with an optional OneDrive/SharePoint
 * link. Reads are open to any signed-in staff member; leadership/admin get
 * create / edit (a plain textarea for body_md) / delete. Everyone else is
 * read-only (useAuth().hasRole).
 *
 * DECISION (Tim): native markdown docs + optional OneDrive/SharePoint links.
 *
 * No markdown library is a dependency, so markdown is rendered by a small,
 * SAFE renderer (renderMarkdown) that escapes all HTML first and only emits a
 * fixed set of React elements — never dangerouslySetInnerHTML on raw input.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardBody } from '~/components/ui';
import { useAuth } from '~/lib/auth';
import {
  listKnowledge, createFolder, createDoc, updateDoc, deleteDoc,
  type KnowledgeFolder, type KnowledgeDoc,
} from '~/lib/api/knowledge';
import { renderMarkdown } from '~/lib/markdown';

export const Route = createFileRoute('/_app/ninety/knowledge')({
  component: KnowledgeBrowser,
});

// "Root" = the implicit bucket for folder-less docs.
const ROOT = '__root__';

function KnowledgeBrowser() {
  const auth = useAuth();
  const canEdit = auth.hasRole('leadership') || auth.hasRole('admin');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['knowledge'],
    queryFn: listKnowledge,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['knowledge'] });

  const folders = data?.folders ?? [];
  const docs = data?.docs ?? [];

  const [selectedFolder, setSelectedFolder] = useState<string>(ROOT);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  // Docs in the selected folder (ROOT = folder_id null).
  const folderDocs = useMemo(() => {
    return docs
      .filter((d) => (selectedFolder === ROOT ? d.folder_id === null : d.folder_id === selectedFolder))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [docs, selectedFolder]);

  const selectedDoc = useMemo(
    () => docs.find((d) => d.id === selectedDocId) ?? null,
    [docs, selectedDocId],
  );

  // ── mutations ──────────────────────────────────────────────────────────
  const addFolder = useMutation({
    mutationFn: (name: string) => createFolder({ name, parent_folder_id: selectedFolder === ROOT ? null : selectedFolder }),
    onSuccess: invalidate,
  });
  const addDoc = useMutation({
    mutationFn: (title: string) => createDoc({ title, folder_id: selectedFolder === ROOT ? null : selectedFolder }),
    onSuccess: (res) => { setSelectedDocId(res.doc.id); setEditing(true); invalidate(); },
  });
  const saveDoc = useMutation({
    mutationFn: (vars: { id: string; body: Parameters<typeof updateDoc>[1] }) => updateDoc(vars.id, vars.body),
    onSuccess: () => { setEditing(false); invalidate(); },
  });
  const removeDoc = useMutation({
    mutationFn: (id: string) => deleteDoc(id),
    onSuccess: () => { setSelectedDocId(null); setEditing(false); invalidate(); },
  });

  return (
    <div className="px-4 sm:px-6 py-4">
      {error && (
        <p className="text-sm text-red-600 mb-3">{(error as Error).message}</p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        {/* Left: folder tree + doc list */}
        <div className="space-y-3">
          <Card>
            <CardBody className="space-y-2">
              <h2 className="text-xs uppercase tracking-wide font-bold text-[var(--color-ink-soft)]">Folders</h2>
              <FolderTree
                folders={folders}
                selected={selectedFolder}
                onSelect={(id) => { setSelectedFolder(id); setSelectedDocId(null); setEditing(false); }}
              />
              {canEdit && (
                <InlineAdd
                  placeholder="New folder…"
                  buttonLabel="Add folder"
                  pending={addFolder.isPending}
                  onAdd={(v) => addFolder.mutate(v)}
                />
              )}
            </CardBody>
          </Card>

          <Card>
            <CardBody className="space-y-2">
              <h2 className="text-xs uppercase tracking-wide font-bold text-[var(--color-ink-soft)]">Docs</h2>
              {isLoading ? (
                <p className="text-sm text-[var(--color-ink-muted)]">Loading…</p>
              ) : folderDocs.length === 0 ? (
                <p className="text-sm text-[var(--color-ink-muted)]">No docs in this folder.</p>
              ) : (
                <ul className="space-y-1">
                  {folderDocs.map((d) => (
                    <li key={d.id}>
                      <button
                        onClick={() => { setSelectedDocId(d.id); setEditing(false); }}
                        className={
                          'w-full text-left rounded-md px-2 py-1.5 text-sm ' +
                          (d.id === selectedDocId
                            ? 'bg-[var(--color-navy)] text-white'
                            : 'hover:bg-black/5')
                        }
                      >
                        {d.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {canEdit && (
                <InlineAdd
                  placeholder="New doc…"
                  buttonLabel="Add doc"
                  pending={addDoc.isPending}
                  onAdd={(v) => addDoc.mutate(v)}
                />
              )}
            </CardBody>
          </Card>
        </div>

        {/* Right: viewer / editor */}
        <Card>
          <CardBody>
            {!selectedDoc ? (
              <p className="text-sm text-[var(--color-ink-muted)]">Select a doc to read it.</p>
            ) : editing && canEdit ? (
              <DocEditor
                doc={selectedDoc}
                saving={saveDoc.isPending}
                onCancel={() => setEditing(false)}
                onSave={(body) => saveDoc.mutate({ id: selectedDoc.id, body })}
              />
            ) : (
              <DocViewer
                doc={selectedDoc}
                canEdit={canEdit}
                onEdit={() => setEditing(true)}
                onDelete={() => { if (confirm(`Delete "${selectedDoc.title}"?`)) removeDoc.mutate(selectedDoc.id); }}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

// ── Folder tree (self-referential; rendered recursively) ───────────────────
function FolderTree({
  folders, selected, onSelect,
}: {
  folders: KnowledgeFolder[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  const childrenOf = useMemo(() => {
    const map: Record<string, KnowledgeFolder[]> = {};
    for (const f of folders) {
      const key = f.parent_folder_id ?? ROOT;
      (map[key] ??= []).push(f);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
    }
    return map;
  }, [folders]);

  const renderLevel = (parentKey: string, depth: number): React.ReactNode => {
    const list = childrenOf[parentKey] ?? [];
    if (list.length === 0) return null;
    return (
      <ul className="space-y-0.5">
        {list.map((f) => (
          <li key={f.id}>
            <button
              onClick={() => onSelect(f.id)}
              style={{ paddingLeft: `${8 + depth * 14}px` }}
              className={
                'w-full text-left rounded-md py-1 pr-2 text-sm ' +
                (f.id === selected ? 'bg-[var(--color-navy)] text-white' : 'hover:bg-black/5')
              }
            >
              {f.name}
            </button>
            {renderLevel(f.id, depth + 1)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div>
      <button
        onClick={() => onSelect(ROOT)}
        className={
          'w-full text-left rounded-md px-2 py-1 text-sm font-medium ' +
          (selected === ROOT ? 'bg-[var(--color-navy)] text-white' : 'hover:bg-black/5')
        }
      >
        All docs
      </button>
      {renderLevel(ROOT, 0)}
    </div>
  );
}

// ── Doc viewer ─────────────────────────────────────────────────────────────
function DocViewer({
  doc, canEdit, onEdit, onDelete,
}: {
  doc: KnowledgeDoc;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <article className="space-y-3">
      <div className="flex items-start gap-3">
        <h1 className="flex-1 text-2xl font-bold text-[var(--color-navy)]">{doc.title}</h1>
        {canEdit && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="ghost" onClick={onEdit}>Edit</Button>
            <Button size="sm" variant="ghost" onClick={onDelete}>Delete</Button>
          </div>
        )}
      </div>

      {doc.onedrive_item_id && (
        <a
          href={`https://my.sharepoint.com/_layouts/15/Doc.aspx?sourcedoc=${encodeURIComponent(doc.onedrive_item_id)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block text-sm text-[var(--color-navy)] underline"
        >
          Open in OneDrive / SharePoint ↗
        </a>
      )}

      {doc.body_md && doc.body_md.trim()
        ? <div className="space-y-2 text-sm leading-relaxed">{renderMarkdown(doc.body_md)}</div>
        : <p className="text-sm text-[var(--color-ink-muted)]">This doc has no content yet.</p>}
    </article>
  );
}

// ── Doc editor (leadership/admin) ──────────────────────────────────────────
function DocEditor({
  doc, saving, onSave, onCancel,
}: {
  doc: KnowledgeDoc;
  saving: boolean;
  onSave: (body: { title: string; body_md: string; onedrive_item_id: string | null }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(doc.title);
  const [bodyMd, setBodyMd] = useState(doc.body_md ?? '');
  const [oneDrive, setOneDrive] = useState(doc.onedrive_item_id ?? '');

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        const t = title.trim();
        if (!t) return;
        onSave({ title: t, body_md: bodyMd, onedrive_item_id: oneDrive.trim() || null });
      }}
    >
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Doc title"
        className="w-full rounded-md border border-[var(--color-line)] px-3 py-2 text-lg font-semibold focus:outline-none focus:border-[var(--color-navy)]"
      />
      <input
        value={oneDrive}
        onChange={(e) => setOneDrive(e.target.value)}
        placeholder="OneDrive / SharePoint item id (optional)"
        className="w-full rounded-md border border-[var(--color-line)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-navy)]"
      />
      <textarea
        value={bodyMd}
        onChange={(e) => setBodyMd(e.target.value)}
        placeholder="Write the doc in Markdown…"
        rows={18}
        className="w-full rounded-md border border-[var(--color-line)] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--color-navy)]"
      />
      <div className="flex gap-2">
        <Button type="submit" size="sm" variant="primary" disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}

// ── small inline "add" form reused for folders + docs ──────────────────────
function InlineAdd({
  placeholder, buttonLabel, pending, onAdd,
}: {
  placeholder: string;
  buttonLabel: string;
  pending: boolean;
  onAdd: (value: string) => void;
}) {
  const [value, setValue] = useState('');
  return (
    <form
      className="flex gap-2 pt-1"
      onSubmit={(e) => { e.preventDefault(); const v = value.trim(); if (v) { onAdd(v); setValue(''); } }}
    >
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        className="flex-1 min-w-0 rounded-md border border-[var(--color-line)] px-2 py-1 text-xs focus:outline-none focus:border-[var(--color-navy)]"
      />
      <Button type="submit" size="sm" variant="ghost" disabled={pending || !value.trim()}>{buttonLabel}</Button>
    </form>
  );
}
